import i18n from 'i18next';
import renderA11yString from 'katex/contrib/render-a11y-string';

import { reactionSpeech } from './reaction-speech';

/**
 * What a formula SOUNDS like, for the read-through.
 *
 * ── WHY THIS IS NOT `textContent` ─────────────────────────────────────────────────────────────
 * A rendered formula's text is unusable, in two compounding ways, and both were measured:
 *
 * KaTeX nests `<annotation encoding="application/x-tex">` inside `<math>`, so reading the text of
 * `\frac{1}{2}` yields `"12\frac{1}{2}21"` — the MathML characters, then the LaTeX source, then the
 * visual glyphs. Every formula would be spoken three times, once as markup.
 *
 * And mhchem builds every chemical subscript as `<msub><mpadded><mphantom><mi>X</mi></mphantom>…`.
 * `mphantom` means "reserve the space, render nothing", so a conforming screen reader is correctly
 * silent on it — this is NOT a defect in what students hear. But any text extraction announces H2O
 * as "H X 2 O". The phantom is a trap for us, not for them.
 *
 * ── WHAT A STUDENT ACTUALLY HEARS ─────────────────────────────────────────────────────────────
 * KaTeX emits real MathML alongside an `aria-hidden` visual layer, so a MathML-capable reader gets
 * the structure and says "one half". Where MathML support is weak the reading collapses instead:
 * the MathML text for `\frac{1}{2}` is "12", spoken as "twelve", and `x_1^2` is "x12". We cannot fix
 * that from here — it is the reader's capability — so this module aims at the teacher's own question
 * instead: does my content make sense heard at all.
 */

/** `\textcolor{...}{content}` → `content`, repeatedly, so nesting unwinds. */
function stripLatexColor(latex: string): string {
  let out = latex;
  for (let i = 0; i < 8; i++) {
    const next = out.replace(/\\(?:textcolor|color)\s*\{[^{}]*\}\s*\{([^{}]*)\}/g, '$1');
    if (next === out) return next;
    out = next;
  }
  return out;
}

/**
 * A math formula as a spoken sentence.
 *
 * `renderA11yString` is KaTeX's own generator and is genuinely good — `\frac{1}{2}` becomes
 * "start fraction, 1, divided by, 2, end fraction". Two things it needs help with:
 *
 * COLOUR LEAKS. `\textcolor{#CE8508}{y=5x+3}` comes back as "start color #CE8508, y, equals, 5, x,
 * plus, 3, end color #CE8508". Reading a hex code aloud is worse than reading nothing, and coloured
 * formulas are a supported feature, so colour is stripped before it ever sees the source.
 *
 * IT THROWS. Unsupported constructs raise rather than degrade — every `\ce{}` fails with "vphantom
 * not implemented yet", which is why chemistry has its own path below. Anything else that throws
 * falls back to the authored source, which is at least what the teacher typed.
 */
export function mathAnnouncement(latex: string): string {
  const src = stripLatexColor(latex).trim();
  if (!src) return '';
  try {
    const spoken = renderA11yString(src).replace(/\s+/g, ' ').trim();
    return spoken || src;
  } catch {
    return src;
  }
}

/**
 * mhchem's operators, longest first, and the order is load-bearing in two places specifically:
 * `<=>>` and `<<=>` must precede `<=>`, and `<->` must precede `<-`, since each shorter pattern
 * matches inside the longer one. (`<=>` and `<-` do NOT collide — `<=>` is `<`, `=`, `>` — so
 * their relative order is free. Both traps are covered in the tests.)
 */
const CHEM_OPERATORS: [RegExp, string][] = [
  [/<=>>/g, ', in equilibrium with, favouring the products, '],
  [/<<=>/g, ', in equilibrium with, favouring the reactants, '],
  [/<=>/g, ', in equilibrium with, '],
  [/<->/g, ', resonance with, '],
  [/->/g, ', yields, '],
  [/<-/g, ', is produced from, '],
  // The operator plus only, never a charge: mhchem writes `Na+ + Cl-`, so the spaced one joins
  // reactants and the attached one is part of the species.
  [/\s\+\s/g, ', plus, '],
];

/**
 * A reaction as a spoken sentence — from the AUTHORED string, not the rendering.
 *
 * `renderA11yString` cannot help here: it throws on every `\ce{}`. The authored form is the best
 * source anyway, because the rendered LaTeX has been split at operators into
 * `\ce{2H2O}\allowbreak \ce{-> 2H2 +}\allowbreak \ce{O2}` with `->` entity-escaped, so it is both
 * fragmented and mangled, while `data-reaction-formula` holds exactly what was typed.
 *
 * Only the OPERATORS are expanded — arrows and the joining plus. They are the parts a reader either
 * mispronounces ("rightwards arrow") or drops entirely at low symbol verbosity, and the only parts
 * with an unambiguous reading. Species are left alone: see `chemReadsAmbiguously` for why they
 * cannot be fixed here.
 *
 * The result is COMMA-DELIMITED, which is the whole pacing fix. `render-a11y-string` delimits maths
 * the same way ("y, equals, 5, x") and speech engines turn a comma into a pause, which is exactly
 * why equations read at a sensible speed and reactions ran together into one breathless word.
 */
export function reactionAnnouncement(formula: string): string {
  let out = formula.trim();
  if (!out) return '';
  for (const [pattern, phrase] of CHEM_OPERATORS) out = out.replace(pattern, phrase);
  return out
    .replace(/\s+/g, ' ')
    .replace(/\s+,/g, ',')          // the space that sat before the operator we replaced
    .replace(/,\s*(?=,)/g, '')      // two operators in a row leave a double comma
    .replace(/^,\s*|,\s*$/g, '')
    .trim();
}

export interface FormulaAnnouncement {
  /** The sentence to show or speak. Falls back to the authored source, never to `textContent`. */
  name: string;
}

/**
 * The announcement for a rendered formula node, math or chemistry.
 *
 * Reads the AUTHORED source off the node's own attribute. An `aria-label` still wins if one is ever
 * set, since that would be what a screen reader uses.
 */
/**
 * A REACTION, named as one — because its role calls it a picture.
 *
 * The node is `role='img'` so that KaTeX's presentation MathML is not read a glyph at a time (see
 * `processReactionHTML`), and that role makes a reader announce "graphic" after the sentence. A
 * chemical equation announced as a graphic is wrong in the same way an animation announced as a
 * still one was wrong: the role asserts something false about the medium, which is worse than
 * saying nothing.
 *
 * So the word goes in the LABEL, which we author ourselves — the sanctioned route, and the one
 * that means a real reader says it rather than only our emulator. The read-through then reproduces
 * it by reading the name, with no special case: see `SPOKEN_KINDS`.
 *
 * EQUATIONS are deliberately left out of this. Their MathML is read by a maths engine that does
 * the job better than any string here, and the only way to attach a prefix is an `aria-label`,
 * which would override that engine — trading a purpose-built maths reader for a flat sentence we
 * generated. Accepting the gap on readers with no maths support is the cheaper mistake; closing it
 * properly means `role="math"` semantics, tested against a real reader first.
 *
 * Applied wherever a reaction is announced, not only inline: a standalone one reads the same to a
 * listener, and only the teacher-facing chip differs between the two.
 */
export function reactionLabel(formula: string): string {
  const spoken = reactionSpeech(formula);
  if (!spoken) return '';
  /*
   * NOT EVERY NODE IS A REACTION. The `data-reaction-formula` node holds any mhchem content, and
   * plenty of it is a lone species — `H2O` inline in a sentence. Calling that "Reaction: H 2 O" is
   * simply false, and a wrong label is worse than none: it tells a listener to expect reactants
   * and products that never arrive.
   *
   * Decided from the AUTHORED string rather than the spoken one, because the spoken connectives
   * are translated and this must not depend on the interface language. An operator is what makes
   * a reaction a reaction; without one it is a chemical formula, which is the same distinction the
   * editor already draws between its two node types.
   */
  const key = CHEM_OPERATORS.some(([pattern]) => new RegExp(pattern.source).test(formula))
    ? 'blocks.toolbar.reactionSpoken'
    : 'blocks.toolbar.chemFormulaSpoken';
  return i18n.t(key, { reaction: spoken });
}

export function formulaAnnouncement(el: Element): FormulaAnnouncement {
  const aria = (el.getAttribute('aria-label') ?? '').trim();
  if (aria) return { name: aria };

  const chem = (el.getAttribute('data-reaction-formula') ?? '').trim();
  if (chem) {
    // The SAME derivation the node's `aria-label` carries, so the read-through previews what a
    // screen reader is actually given rather than running a second implementation beside it. The
    // `aria-label` above already wins when one is present; this covers a node rendered without
    // `processReactionHTML`, and keeps the two in step by construction.
    return { name: reactionLabel(chem) };
  }
  return { name: mathAnnouncement((el.getAttribute('data-math-latex') ?? '').trim()) };
}
